/**
 * Darts-Cricket's board on the green (app/LagKing/DARTS_CRICKET.md, Cole
 * 2026-10-20): each number is a band 3 ft deep down the putt line, drawn as a
 * fan like a dartboard wedge. The triple wedge sits in the middle, a double
 * wedge each side of it, single wedges outside, all widening with distance
 * because the multiplier is the start-line ANGLE, not a lateral distance.
 * Every number is colored the same: a green triple, yellow doubles, red
 * singles, solid, with no lines between or around the lanes (Cole,
 * 2026-10-21: a ball on a white line did not read as in either lane). A
 * real bullseye stands on the line at 55 ft: a green outer bull and a red
 * inner bull.
 *
 * The host sends the board as data (`PuttSceneDartBoard`); this module turns
 * it into flat triangles batched by color, so the phone and both TV receivers
 * draw exactly the same board from the same few numbers. Lateral offsets come
 * from `lateralAt`, the projection the rolling ball uses, so a ball is drawn
 * resting in the wedge it is scored in.
 *
 * Pure: three.js-free, so node tests check the geometry directly.
 */
import { lateralAt } from './puttMath';

export interface PuttSceneDartBand {
  id: string;
  minFt: number;
  maxFt: number;
  /** Retired 2026-10-20 (every band is colored the same now); still read so
   *  an older phone's board draws. */
  tone?: 0 | 1;
  /** Closed for everyone: the fan is not painted, so the green shows. */
  closed?: boolean;
}

export interface PuttSceneDartBull {
  centerFt: number;
  outerRadiusFt: number;
  innerRadiusFt: number;
  closed?: boolean;
}

export interface PuttSceneDartBoard {
  /** |start line| at or inside this is a triple. */
  tripleDeg: number;
  /** |start line| at or inside this (and outside the triple) is a double. */
  doubleDeg: number;
  bands: PuttSceneDartBand[];
  bull?: PuttSceneDartBull | null;
}

/**
 * The singles have no lateral limit (any start line wider than the double is
 * a single); they are drawn to this angle so they can be drawn at all. At 48
 * ft that is 6.7 ft either side, inside the standing labels' rails (9 ft).
 * Presentation only: never read back as a scoring edge.
 */
export const DART_SINGLE_DRAW_DEG = 8;


/** Half-width of the drawn board at a distance (ft): the singles' outer edge. */
/** A board color as the key swatch shows it: the singles' red at their
 *  translucency (#RRGGBBAA), the others solid. */
export function dartKeySwatchColor(ring: 'triple' | 'double' | 'single'): string {
  if (ring !== 'single') return DART_COLORS[ring];
  return `${DART_COLORS.single}${Math.round(DART_SINGLE_OPACITY * 255).toString(16).padStart(2, '0').toUpperCase()}`;
}

export function dartBoardEdgeFt(atFt: number, board?: Pick<PuttSceneDartBoard, 'doubleDeg'>): number {
  return lateralAt(atFt, Math.max(board?.doubleDeg ?? 0, DART_SINGLE_DRAW_DEG));
}

/** How far outside the drawn edge a standing label's inner edge sits (ft). */
export const DART_LABEL_GAP_FT = 0.45;

/** Board colors, chosen to read on the turf from the address view. */
export const DART_COLORS = {
  /** The same three colors on every number (Cole, 2026-10-20: "The middle
   *  should be green, doubles should be yellow, singles should be red. Don't
   *  alternate the colors of the bands."). The green is a saturated
   *  emerald, well apart from the turf. */
  triple: '#00D26A',
  double: '#FFD21F',
  single: '#E5262E',
  bullOuter: '#00D26A',
  bullInner: '#E5262E',
} as const;



/** The singles are very see-through (Cole, 2026-10-20: "red is a little too
 *  harsh, make it very transparent"); the outline keeps the edge crisp. */
export const DART_SINGLE_OPACITY = 0.25;
const SINGLE_OPACITY = DART_SINGLE_OPACITY;
const RING_OPACITY = 0.9;
const BULL_SEGMENTS = 48;

export interface DartBoardGroup {
  color: string;
  opacity: number;
  /** Flat XZ triangles (y = 0), 3 components per vertex. */
  positions: Float32Array;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** The board from a payload, or null when it is absent or malformed. */
export function normalizeDartBoard(value: unknown): PuttSceneDartBoard | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (!finite(raw.tripleDeg) || !finite(raw.doubleDeg)) return null;
  const tripleDeg = Math.max(0, raw.tripleDeg);
  const doubleDeg = Math.max(tripleDeg, raw.doubleDeg);
  const bands: PuttSceneDartBand[] = [];
  for (const item of Array.isArray(raw.bands) ? raw.bands.slice(0, 12) : []) {
    if (!item || typeof item !== 'object') continue;
    const band = item as Record<string, unknown>;
    if (typeof band.id !== 'string' || !finite(band.minFt) || !finite(band.maxFt)) continue;
    if (band.minFt < 0 || band.maxFt <= band.minFt || band.maxFt > 80) continue;
    bands.push({
      id: band.id,
      minFt: band.minFt,
      maxFt: band.maxFt,
      tone: band.tone === 1 ? 1 : 0,
      ...(band.closed === true ? { closed: true } : {}),
    });
  }
  let bull: PuttSceneDartBull | null = null;
  const rawBull = raw.bull as Record<string, unknown> | null | undefined;
  if (rawBull && typeof rawBull === 'object' && finite(rawBull.centerFt) && finite(rawBull.outerRadiusFt) && finite(rawBull.innerRadiusFt)) {
    const outer = Math.min(5, Math.max(0.1, rawBull.outerRadiusFt));
    const inner = Math.min(outer, Math.max(0, rawBull.innerRadiusFt));
    if (rawBull.centerFt > 0 && rawBull.centerFt <= 80) {
      bull = { centerFt: rawBull.centerFt, outerRadiusFt: outer, innerRadiusFt: inner, ...(rawBull.closed === true ? { closed: true } : {}) };
    }
  }
  if (!bands.length && !bull) return null;
  return { tripleDeg, doubleDeg, bands, bull };
}

/** A stable string for memo keys: equal boards give equal keys. */
export function dartBoardKey(board: PuttSceneDartBoard | null): string {
  return board ? JSON.stringify(board) : '';
}

/** One band's wedges, left to right: single, double, triple, double, single. */
export function dartBandWedges(board: Pick<PuttSceneDartBoard, 'tripleDeg' | 'doubleDeg'>): Array<{ fromDeg: number; toDeg: number; ring: 'single' | 'double' | 'triple' }> {
  const t = board.tripleDeg;
  const d = board.doubleDeg;
  const s = Math.max(d, DART_SINGLE_DRAW_DEG);
  return [
    { fromDeg: -s, toDeg: -d, ring: 'single' },
    { fromDeg: -d, toDeg: -t, ring: 'double' },
    { fromDeg: -t, toDeg: t, ring: 'triple' },
    { fromDeg: t, toDeg: d, ring: 'double' },
    { fromDeg: d, toDeg: s, ring: 'single' },
  ];
}

function pushQuad(out: number[], nearFt: number, farFt: number, fromDeg: number, toDeg: number): void {
  if (!(toDeg > fromDeg)) return;
  const a0 = lateralAt(nearFt, fromDeg);
  const b0 = lateralAt(nearFt, toDeg);
  const a1 = lateralAt(farFt, fromDeg);
  const b1 = lateralAt(farFt, toDeg);
  // Two triangles, wound counterclockwise from above.
  out.push(a0, 0, -nearFt, b0, 0, -nearFt, a1, 0, -farFt);
  out.push(b0, 0, -nearFt, b1, 0, -farFt, a1, 0, -farFt);
}

function pushAnnulus(out: number[], centerFt: number, innerFt: number, outerFt: number): void {
  for (let i = 0; i < BULL_SEGMENTS; i += 1) {
    const a = (i / BULL_SEGMENTS) * Math.PI * 2;
    const b = ((i + 1) / BULL_SEGMENTS) * Math.PI * 2;
    const ox0 = Math.cos(a) * outerFt, oz0 = Math.sin(a) * outerFt;
    const ox1 = Math.cos(b) * outerFt, oz1 = Math.sin(b) * outerFt;
    if (innerFt <= 0) {
      out.push(0, 0, -centerFt, ox0, 0, -centerFt + oz0, ox1, 0, -centerFt + oz1);
      continue;
    }
    const ix0 = Math.cos(a) * innerFt, iz0 = Math.sin(a) * innerFt;
    const ix1 = Math.cos(b) * innerFt, iz1 = Math.sin(b) * innerFt;
    out.push(ix0, 0, -centerFt + iz0, ox0, 0, -centerFt + oz0, ox1, 0, -centerFt + oz1);
    out.push(ix0, 0, -centerFt + iz0, ox1, 0, -centerFt + oz1, ix1, 0, -centerFt + iz1);
  }
}

/** The board as triangles batched by color and opacity (one mesh per group). */
export function dartBoardGroups(board: PuttSceneDartBoard): DartBoardGroup[] {
  const groups = new Map<string, { color: string; opacity: number; vertices: number[] }>();
  const add = (color: string, opacity: number) => {
    const key = `${color}|${opacity}`;
    let group = groups.get(key);
    if (!group) {
      group = { color, opacity, vertices: [] };
      groups.set(key, group);
    }
    return group.vertices;
  };
  const wedges = dartBandWedges(board);
  // A number closed for everyone is simply not painted: the green shows
  // through (Cole, 2026-10-23: "just remove the shading from the green, dont
  // black it out").
  for (const band of board.bands) {
    if (band.closed) continue;
    for (const wedge of wedges) {
      if (wedge.ring === 'triple' && board.tripleDeg <= 0) continue;
      const opacity = wedge.ring === 'single' ? SINGLE_OPACITY : RING_OPACITY;
      pushQuad(add(DART_COLORS[wedge.ring], opacity), band.minFt, band.maxFt, wedge.fromDeg, wedge.toDeg);
    }
  }
  const bull = board.bull;
  if (bull && !bull.closed) {
    pushAnnulus(add(DART_COLORS.bullOuter, RING_OPACITY), bull.centerFt, bull.innerRadiusFt, bull.outerRadiusFt);
    if (bull.innerRadiusFt > 0) {
      pushAnnulus(add(DART_COLORS.bullInner, RING_OPACITY), bull.centerFt, 0, bull.innerRadiusFt);
    }
  }
  return [...groups.values()]
    .filter((group) => group.vertices.length > 0)
    .map((group) => ({ color: group.color, opacity: group.opacity, positions: new Float32Array(group.vertices) }));
}
